import {
  TabItemProps,
  RenderTabProps,
  RenderPanelProps,
  TabPanel,
} from "@cdkglobal/radial";
import { RecordPageRenderContext, JsonSchema } from "../../../../types";
import { StyledTab } from "../../../../utils/styled";
import {
  getAdditionalFieldsForSection,
  getAdditionalSections,
  shouldRenderTab,
  getTabLabel,
} from "../../helpers";
import { renderField } from "./recordPage.field";
import { renderSetupInfo } from "./recordPage.info";
import { renderSection } from "./recordPage.section";

export function renderAdditionalFields(
  sectionKey: string,
  ctx: RecordPageRenderContext,
): JSX.Element | null {
  const schemaForSection = getAdditionalFieldsForSection({
    setupKey: ctx.schema.title,
    sectionKey,
  });

  if (!schemaForSection) return null;

  return (
    <>
      {Object.entries(schemaForSection).map(([propertyKey, explicitProperty]) =>
        renderField(propertyKey, ctx, explicitProperty),
      )}
    </>
  );
}

export function renderAdditionalSections(
  ctx: RecordPageRenderContext,
  position: "top" | "bottom",
  tabKey?: string,
): (JSX.Element | null)[] | null {
  const additionalSections = getAdditionalSections({
    setupName: ctx.schema.title,
    position,
  });
  if (!additionalSections) return null;

  const excludedTabs = ["generalFieldsTab"];
  if (tabKey && excludedTabs.includes(tabKey)) return null;

  return additionalSections.map(
    ({ sectionKey, sectionValue, explicitSectionValueWithProperty }) =>
      renderSection(
        sectionKey,
        sectionValue,
        ctx,
        explicitSectionValueWithProperty,
      ),
  );
}

export function buildTabItems({
  tabKeys,
  schemaSections,
  ctx,
}: {
  tabKeys: string[];
  schemaSections: JsonSchema["sections"];
  ctx: RecordPageRenderContext;
}): TabItemProps[] {
  return tabKeys.map((tabKey, index) => ({
    id: `tab-${index}`,
    panelId: `panel-${index}`,
    renderTab: (props: RenderTabProps) =>
      shouldRenderTab(
        ctx.isRecordOpen || ctx.isSideSheetOpen,
        ctx.schema.title,
      ) ? (
        <StyledTab
          {...props}
          id={`tab-${index}`}
          label={getTabLabel(tabKey, ctx)}
        />
      ) : null,
    renderPanel: (props: RenderPanelProps) => (
      <TabPanel {...props}>
        {renderSetupInfo(ctx)}
        {renderAdditionalSections(ctx, "top", tabKey)}
        {renderSection(tabKey, schemaSections[tabKey], ctx)}
        {renderAdditionalSections(ctx, "bottom", tabKey)}
      </TabPanel>
    ),
  }));
}
